import { useLanguage } from "@/lib/language-context";
import { useApp } from "@/lib/app-context";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle, Copy, MessageCircle, Clock, Bell } from "lucide-react";
import { useState } from "react";

export function SuccessScreen() {
  const { t } = useLanguage();
  const { applicationId, setCurrentScreen, resetApplication } = useApp();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (applicationId) {
      await navigator.clipboard.writeText(applicationId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleGoHome = () => {
    resetApplication();
    setCurrentScreen("home");
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-80px)]">
      {/* Main Content */}
      <div className="flex-1 px-4 py-8">
        <div className="max-w-lg mx-auto text-center">
          {/* Success Icon */}
          <div className="w-24 h-24 rounded-full bg-[#28A745]/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-14 w-14 text-[#28A745]" />
          </div>

          <h1 className="text-3xl font-bold text-foreground mb-3">{t.successTitle}</h1>
          <p className="text-lg text-muted-foreground mb-8">{t.successDesc}</p>

          {/* Application Number Card */}
          <Card className="p-6 mb-6 bg-secondary border-2 border-primary">
            <p className="text-sm text-muted-foreground mb-2">{t.applicationNumber}</p>
            <div className="flex items-center justify-center gap-3">
              <p className="text-2xl font-bold text-primary font-mono">{applicationId}</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopy}
                className="h-10 w-10 p-0"
                aria-label="Copy application number"
              >
                <Copy className={`h-5 w-5 ${copied ? "text-[#28A745]" : "text-muted-foreground"}`} />
              </Button>
            </div>
            {copied && (
              <p className="text-sm text-[#28A745] mt-2">Copied!</p>
            )}
            <p className="text-sm text-muted-foreground mt-3">{t.saveThisNumber}</p>
          </Card>

          {/* What's Next */}
          <Card className="p-6 text-left border-0 bg-muted">
            <h3 className="text-lg font-bold text-foreground mb-4">{t.whatNext}</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground">{t.nextStep1}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Bell className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground">{t.nextStep2}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#28A745]/10 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-[#28A745]" />
                </div>
                <div>
                  <p className="font-medium text-foreground">{t.nextStep3}</p>
                </div>
              </div>
            </div>
          </Card>

          {/* WhatsApp Contact */}
          <a
            href={`https://wa.me/${t.phoneNumberRaw}?text=Hi, I just submitted my visa application. My application number is ${applicationId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 mt-6 p-4 bg-[#25D366] text-white rounded-xl font-bold text-lg hover:opacity-90 transition-opacity"
          >
            <MessageCircle className="h-6 w-6" />
            {t.whatsappUs}
          </a>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="sticky bottom-0 p-4 bg-card border-t border-border safe-area-inset-bottom">
        <div className="max-w-lg mx-auto">
          <Button
            onClick={handleGoHome}
            variant="outline"
            size="lg"
            className="w-full h-14 text-xl font-bold rounded-xl border-2 bg-transparent"
          >
            {t.goHome}
          </Button>
        </div>
      </div>
    </div>
  );
}
